import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useNavigation } from '../../contexts/NavigationContext';
import { userCan } from '../../utils/permissions';
import {
  UsersIcon,
  ChatBubbleLeftRightIcon,
  UserGroupIcon,
  DocumentTextIcon,
  ChartBarIcon,
  CreditCardIcon,
  ChatBubbleOvalLeftIcon,
  UserIcon,
  CpuChipIcon,
  Cog6ToothIcon,
  GiftIcon,
  ShieldCheckIcon,
  SparklesIcon,
  BellIcon
} from '@heroicons/react/24/outline';
import Footer from '../Footer';

export const Sidebar: React.FC = () => {
  const { isSidebarCollapsed, setIsSidebarCollapsed, user } = useNavigation();
  const location = useLocation();
  const [isSMSDropdownOpen, setIsSMSDropdownOpen] = useState(false);
  const [isContactsDropdownOpen, setIsContactsDropdownOpen] = useState(false);
  const [isTemplatesDropdownOpen, setIsTemplatesDropdownOpen] = useState(false);
  const canAccessMessaging = userCan(user, 'messaging', 'read');
  const canManageContacts = userCan(user, 'contacts', 'read');
  const canManageTemplates = userCan(user, 'templates', 'read');
  const canViewReports = userCan(user, 'reports', 'read');
  const canManageUserManagement = userCan(user, 'user_management', 'read');
  const canAccessSubscriptions = userCan(user, 'subscriptions', 'read');
  const canAccessSettings = userCan(user, 'settings', 'read');
  // Only show Super Admin menu for users with super_admin role
  const canViewSuperAdmin = user?.role === 'super_admin';
  const [isReportsDropdownOpen, setIsReportsDropdownOpen] = useState(false);
  const [isSettingsDropdownOpen, setIsSettingsDropdownOpen] = useState(false);
  const [isSuperAdminDropdownOpen, setIsSuperAdminDropdownOpen] = useState(false);

  const isActiveRoute = (path: string) => location.pathname === path;

  return (
    <div className="flex flex-col min-h-screen">
      <div className={`${isSidebarCollapsed ? 'w-16' : 'w-64'} bg-white border-r flex-grow transition-all duration-300 relative`}>
        {/* Toggle Button */}
        <button 
          className="absolute -right-3 top-4 bg-white border border-gray-200 rounded-full p-1.5 hover:bg-gray-50"
          onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        >
          <svg 
            className={`w-4 h-4 text-gray-500 transition-transform duration-300 ${isSidebarCollapsed ? 'rotate-180' : ''}`}
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        {/* Logo */}
        <div className={`p-6 border-b ${isSidebarCollapsed ? 'px-4' : ''}`}>
          <Link to="/dashboard" className="block hover:opacity-90 transition-opacity">
            <img 
              src="/images/logo.jpg" 
              alt="Rukuse Technology" 
              className="h-16 w-auto"
            />
          </Link>
        </div>

        {/* Welcome Message */}
        {!isSidebarCollapsed && (
          <div className="px-6 py-2 border-b">
            <p className="text-[10px] text-gray-600 font-['Helvetica_Neue']">
              Welcome, {user?.name}
            </p>
          </div>
        )}

        {/* Navigation Links */}
        <nav className="mt-4">
          {/* Dashboard */}
          <Link 
            to="/dashboard" 
            className={`flex items-center px-6 py-3 ${isActiveRoute('/dashboard') ? 'bg-gray-100 text-black' : 'text-gray-700'} hover:bg-gray-100 hover:text-black`}
          >
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
            </svg>
            {!isSidebarCollapsed && (
              <span className="ml-3 text-[14px] font-bold">Dashboard</span>
            )}
          </Link>

          {/* SMS with Dropdown */}
          {canAccessMessaging && (
          <div>
            <button 
              onClick={() => setIsSMSDropdownOpen(!isSMSDropdownOpen)}
              className={`w-full flex items-center px-6 py-3 ${
                isActiveRoute('/sms') || location.pathname.startsWith('/sms/') 
                  ? 'bg-gray-100 text-black' 
                  : 'text-gray-700'
              } hover:bg-gray-100 hover:text-black`}
            >
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
              {!isSidebarCollapsed && (
                <>
                  <span className="ml-3 text-[14px] font-bold">SMS</span>
                  <svg 
                    className={`ml-auto w-4 h-4 transition-transform duration-200 ${isSMSDropdownOpen ? 'rotate-180' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </>
              )}
            </button>

            {/* SMS Dropdown Menu */}
            {!isSidebarCollapsed && isSMSDropdownOpen && (
              <div className="bg-gray-50">
                <div className={`pl-10 space-y-1 ${isSMSDropdownOpen ? 'block' : 'hidden'}`}>
                  <Link 
                    to="/sms" 
                    className={`flex items-center py-2 ${isActiveRoute('/sms') ? 'text-black' : 'text-gray-700'} hover:text-black`}
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                    </svg>
                    <span className="text-[14px] font-bold">Quick SMS</span>
                  </Link>
                  <Link 
                    to="/sms/campaign" 
                    className={`flex items-center py-2 ${isActiveRoute('/sms/campaign') ? 'text-black' : 'text-gray-700'} hover:text-black`}
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                    </svg>
                    <span className="text-[14px] font-bold">SMS Campaign</span>
                  </Link>
                </div>
              </div>
            )}
          </div>
          )}

          {/* Contacts with Dropdown */}
          {canManageContacts && (
          <div>
            <button 
              onClick={() => setIsContactsDropdownOpen(!isContactsDropdownOpen)}
              className={`w-full flex items-center px-6 py-3 ${
                isActiveRoute('/contacts') || location.pathname.startsWith('/contacts/') 
                  ? 'bg-gray-100 text-black' 
                  : 'text-gray-700'
              } hover:bg-gray-100 hover:text-black`}
            >
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              {!isSidebarCollapsed && (
                <>
                  <span className="ml-3 text-[14px] font-bold">Contacts</span>
                  <svg 
                    className={`ml-auto w-4 h-4 transition-transform duration-200 ${isContactsDropdownOpen ? 'rotate-180' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </>
              )}
            </button>

            {/* Contacts Dropdown Menu */}
            {!isSidebarCollapsed && isContactsDropdownOpen && (
              <div className="bg-gray-50">
                {/* New Contact */}
                <Link 
                  to="/contacts/new" 
                  className={`flex items-center pl-14 pr-6 py-2 ${
                    isActiveRoute('/contacts/new') ? 'bg-gray-100 text-black' : 'text-gray-700'
                  } hover:bg-gray-100 hover:text-black`}
                >
                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                  </svg>
                  <span className="ml-3 text-[14px] font-bold">New Contact</span>
                </Link>

                {/* My Groups */}
                <Link 
                  to="/contacts/groups" 
                  className={`flex items-center pl-14 pr-6 py-2 ${
                    isActiveRoute('/contacts/groups') ? 'bg-gray-100 text-black' : 'text-gray-700'
                  } hover:bg-gray-100 hover:text-black`}
                >
                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="ml-3 text-[14px] font-bold">My Groups</span>
                </Link>
              </div>
            )}
          </div>
          )}

          {/* Templates with Dropdown */}
          {canManageTemplates && (
          <div>
            <button 
              onClick={() => setIsTemplatesDropdownOpen(!isTemplatesDropdownOpen)}
              className={`w-full flex items-center px-6 py-3 ${
                isActiveRoute('/templates') || location.pathname.startsWith('/templates/') 
                  ? 'bg-gray-100 text-black' 
                  : 'text-gray-700'
              } hover:bg-gray-100 hover:text-black`}
            >
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6z" />
              </svg>
              {!isSidebarCollapsed && (
                <>
                  <span className="ml-3 text-[14px] font-bold">Templates</span>
                  <svg 
                    className={`ml-auto w-4 h-4 transition-transform duration-200 ${isTemplatesDropdownOpen ? 'rotate-180' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </>
              )}
            </button>

            {/* Templates Dropdown Menu */}
            {!isSidebarCollapsed && isTemplatesDropdownOpen && (
              <div className="bg-gray-50">
                {/* Create Template */}
                <Link 
                  to="/templates/create" 
                  className={`flex items-center pl-14 pr-6 py-2 ${
                    isActiveRoute('/templates/create') ? 'bg-gray-100 text-black' : 'text-gray-700'
                  } hover:bg-gray-100 hover:text-black`}
                >
                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16m8-8H4" />
                  </svg>
                  <span className="ml-3 text-[14px] font-bold">Create Template</span>
                </Link>

                {/* Manage Templates (delinked from sidebar; keep route available) */}
                {/*
                <Link 
                  to="/templates/manage" 
                  className={`flex items-center pl-14 pr-6 py-2 ${
                    isActiveRoute('/templates/manage') ? 'bg-gray-100 text-black' : 'text-gray-700'
                  } hover:bg-gray-100 hover:text-black`}
                >
                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  <span className="ml-3 text-[14px] font-bold">Manage Templates</span>
                </Link>
                */}
              </div>
            )}
          </div>
          )}

          {/* Reports Link */}
          {canAccessMessaging && (
            <Link 
              to="/sms/logs" 
              className={`flex items-center px-6 py-3 ${isActiveRoute('/sms/logs') ? 'bg-gray-100 text-black' : 'text-gray-700'} hover:bg-gray-100 hover:text-black`}
            >
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-8 4h8M7 8h10M6 5h12a1 1 0 011 1v12a1 1 0 01-1 1H6a1 1 0 01-1-1V6a1 1 0 011-1z" />
              </svg>
              {!isSidebarCollapsed && (
                <span className="ml-3 text-[14px] font-bold">SMS Logs</span>
              )}
            </Link>
          )}



          {/* Settings with Dropdown */}
          {canAccessSettings && (
          <div>
            <button 
              onClick={() => setIsSettingsDropdownOpen(!isSettingsDropdownOpen)}
              className={`w-full flex items-center px-6 py-3 ${
                isActiveRoute('/settings') || location.pathname.startsWith('/settings/') 
                  ? 'bg-gray-100 text-black' 
                  : 'text-gray-700'
              } hover:bg-gray-100 hover:text-black`}
            >
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {!isSidebarCollapsed && (
                <>
                  <span className="ml-3 text-[14px] font-bold">Settings</span>
                  <svg 
                    className={`ml-auto w-4 h-4 transform transition-transform duration-200 ${isSettingsDropdownOpen ? 'rotate-180' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </>
              )}
            </button>

            {/* Settings Dropdown Menu */}
            {isSettingsDropdownOpen && !isSidebarCollapsed && (
              <div className="bg-gray-50">
                <Link
                  to="/settings/account"
                  className={`pl-14 pr-6 py-3 flex items-center ${isActiveRoute('/settings/account') ? 'bg-gray-100 text-black' : 'text-gray-700'} hover:bg-gray-100 hover:text-black`}
                >
                  <span className="text-[14px]">Account</span>
                </Link>
                <Link
                  to="/settings/notifications"
                  className={`pl-14 pr-6 py-3 flex items-center ${isActiveRoute('/settings/notifications') ? 'bg-gray-100 text-black' : 'text-gray-700'} hover:bg-gray-100 hover:text-black`}
                >
                  <span className="text-[14px]">Notifications</span>
                </Link>
              </div>
            )}
          </div>
          )}

          {/* SuperAdmin with Dropdown */}
          {canViewSuperAdmin && (
          <div>
            <button 
              onClick={() => setIsSuperAdminDropdownOpen(!isSuperAdminDropdownOpen)}
              className={`w-full flex items-center px-6 py-3 ${
                location.pathname.startsWith('/superadmin') ? 'bg-gray-100 text-black' : 'text-gray-700'
              } hover:bg-gray-100 hover:text-black`}
            >
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              {!isSidebarCollapsed && (
                <>
                  <span className="ml-3 text-[14px] font-bold">Super Administrator</span>
                  <svg 
                    className={`ml-auto w-4 h-4 transform transition-transform duration-200 ${
                      isSuperAdminDropdownOpen ? 'rotate-180' : ''
                    }`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </>
              )}
            </button>

            {/* Super Admin Dropdown Menu */}
            {isSuperAdminDropdownOpen && !isSidebarCollapsed && (
              <div className="bg-gray-50">
                <Link 
                  to="/superadmin/users-roles" 
                  className={`flex items-center pl-14 pr-6 py-2 ${
                    isActiveRoute('/superadmin/users-roles') ? 'bg-gray-100 text-black' : 'text-gray-700'
                  } hover:bg-gray-100 hover:text-black`}
                >
                  <UsersIcon className="w-4 h-4 text-gray-500" />
                  <span className="ml-3 text-[14px] font-bold">User and Role Management</span>
                </Link>

                <Link 
                  to="/superadmin/messaging" 
                  className={`flex items-center pl-14 pr-6 py-2 ${
                    isActiveRoute('/superadmin/messaging') ? 'bg-gray-100 text-black' : 'text-gray-700'
                  } hover:bg-gray-100 hover:text-black`}
                >
                  <ChatBubbleOvalLeftIcon className="w-4 h-4 text-gray-500" />
                  <span className="ml-3 text-[14px] font-bold">Messaging Management</span>
                </Link>

                <Link 
                  to="/superadmin/sender-ids" 
                  className={`flex items-center pl-14 pr-6 py-2 ${
                    isActiveRoute('/superadmin/sender-ids') ? 'bg-gray-100 text-black' : 'text-gray-700'
                  } hover:bg-gray-100 hover:text-black`}
                >
                  <ChatBubbleOvalLeftIcon className="w-4 h-4 text-gray-500" />
                  <span className="ml-3 text-[14px] font-bold">Sender ID Management</span>
                </Link>

                <Link 
                  to="/superadmin/sms-providers" 
                  className={`flex items-center pl-14 pr-6 py-2 ${
                    isActiveRoute('/superadmin/sms-providers') ? 'bg-gray-100 text-black' : 'text-gray-700'
                  } hover:bg-gray-100 hover:text-black`}
                >
                  <ChatBubbleOvalLeftIcon className="w-4 h-4 text-gray-500" />
                  <span className="ml-3 text-[14px] font-bold">SMS Providers</span>
                </Link>

                <Link 
                  to="/superadmin/contacts" 
                  className={`flex items-center pl-14 pr-6 py-2 ${
                    isActiveRoute('/superadmin/contacts') ? 'bg-gray-100 text-black' : 'text-gray-700'
                  } hover:bg-gray-100 hover:text-black`}
                >
                  <UserGroupIcon className="w-4 h-4 text-gray-500" />
                  <span className="ml-3 text-[14px] font-bold">Contact Management</span>
                </Link>

                <Link 
                  to="/superadmin/templates" 
                  className={`flex items-center pl-14 pr-6 py-2 ${
                    isActiveRoute('/superadmin/templates') ? 'bg-gray-100 text-black' : 'text-gray-700'
                  } hover:bg-gray-100 hover:text-black`}
                >
                  <DocumentTextIcon className="w-4 h-4 text-gray-500" />
                  <span className="ml-3 text-[14px] font-bold">Template Management</span>
                </Link>

                <Link 
                  to="/superadmin/analytics" 
                  className={`flex items-center pl-14 pr-6 py-2 ${
                    isActiveRoute('/superadmin/analytics') ? 'bg-gray-100 text-black' : 'text-gray-700'
                  } hover:bg-gray-100 hover:text-black`}
                >
                  <ChartBarIcon className="w-4 h-4 text-gray-500" />
                  <span className="ml-3 text-[14px] font-bold">Reporting and Analytics</span>
                </Link>

                <Link 
                  to="/superadmin/ai-analytics" 
                  className={`flex items-center pl-14 pr-6 py-2 ${
                    isActiveRoute('/superadmin/ai-analytics') ? 'bg-gray-100 text-black' : 'text-gray-700'
                  } hover:bg-gray-100 hover:text-black`}
                >
                  <CpuChipIcon className="w-4 h-4 text-gray-500" />
                  <span className="ml-3 text-[14px] font-bold">AI Analytics</span>
                </Link>

                <Link 
                  to="/superadmin/ai-features" 
                  className={`flex items-center pl-14 pr-6 py-2 ${
                    isActiveRoute('/superadmin/ai-features') ? 'bg-gray-100 text-black' : 'text-gray-700'
                  } hover:bg-gray-100 hover:text-black`}
                >
                  <SparklesIcon className="w-4 h-4 text-gray-500" />
                  <span className="ml-3 text-[14px] font-bold">AI Features Admin</span>
                </Link>

                <Link 
                  to="/superadmin/integrations" 
                  className={`flex items-center pl-14 pr-6 py-2 ${
                    isActiveRoute('/superadmin/integrations') ? 'bg-gray-100 text-black' : 'text-gray-700'
                  } hover:bg-gray-100 hover:text-black`}
                >
                  <CpuChipIcon className="w-4 h-4 text-gray-500" />
                  <span className="ml-3 text-[14px] font-bold">Integrations Admin</span>
                </Link>

                <Link 
                  to="/superadmin/subscriptions" 
                  className={`flex items-center pl-14 pr-6 py-2 ${
                    isActiveRoute('/superadmin/subscriptions') ? 'bg-gray-100 text-black' : 'text-gray-700'
                  } hover:bg-gray-100 hover:text-black`}
                >
                  <CreditCardIcon className="w-4 h-4 text-gray-500" />
                  <span className="ml-3 text-[14px] font-bold">Subscription Management</span>
                </Link>

                <Link 
                  to="/superadmin/billing" 
                  className={`flex items-center pl-14 pr-6 py-2 ${
                    isActiveRoute('/superadmin/billing') ? 'bg-gray-100 text-black' : 'text-gray-700'
                  } hover:bg-gray-100 hover:text-black`}
                >
                  <CreditCardIcon className="w-4 h-4 text-gray-500" />
                  <span className="ml-3 text-[14px] font-bold">Billing and Financial Management</span>
                </Link>

                <Link 
                  to="/superadmin/promotions"
                  className={`flex items-center pl-14 pr-6 py-2 text-[14px] ${
                    location.pathname === '/superadmin/promotions'
                      ? 'text-[#FF5722] bg-orange-50'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-black'
                  }`}
                >
                  <GiftIcon className="w-4 h-4" />
                  <span className="ml-3 font-bold">Promotional Code & Referrals</span>
                </Link>

                <Link
                  to="/superadmin/notifications"
                  className={`flex items-center pl-14 pr-6 py-2 text-[14px] ${
                    location.pathname === '/superadmin/notifications'
                      ? 'text-[#FF5722] bg-orange-50'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-black'
                  }`}
                >
                  <BellIcon className="w-4 h-4" />
                  <span className="ml-3 font-bold">Notifications</span>
                </Link>

                <Link
                  to="/superadmin/security"
                  className={`flex items-center pl-14 pr-6 py-2 text-[14px] ${
                    location.pathname === '/superadmin/security'
                      ? 'text-[#FF5722] bg-orange-50'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-black'
                  }`}
                >
                  <ShieldCheckIcon className="w-4 h-4" />
                  <span className="ml-3 font-bold">Platform Security</span>
                </Link>

                <Link 
                  to="/superadmin/settings" 
                  className={`flex items-center pl-14 pr-6 py-2 ${
                    isActiveRoute('/superadmin/settings') ? 'bg-gray-100 text-black' : 'text-gray-700'
                  } hover:bg-gray-100 hover:text-black`}
                >
                  <Cog6ToothIcon className="w-4 h-4 text-gray-500" />
                  <span className="ml-3 text-[14px] font-bold">Super Admin Settings</span>
                </Link>
              </div>
            )}
          </div>
          )}
        </nav>

        {/* Advertisement Banner */}
        {!isSidebarCollapsed && (
          <div className="px-4 mt-8 mb-4">
            <img 
              src="/images/mediumrectangleadvertone.jpg" 
              alt="Advertisement" 
              className="w-full rounded-lg shadow-sm"
            />
          </div>
        )}
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Sidebar; 